'use client';

import { m } from 'framer-motion';
import { startTransition } from 'react';
import { usePopover } from 'minimal-shared/hooks';

import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';

import { newSearchParams } from 'src/routes/paths';
import { useRouter, useSearchParams } from 'src/routes/hooks';

import { useResponsive } from 'src/hooks/use-responsive';

import { fCurrencySymbol } from 'src/utils/format-number';

import { useTranslate } from 'src/locales';

import { Iconify } from 'src/components/iconify';
import { FlagIcon } from 'src/components/flag-icon';
import { useSettingsContext } from 'src/components/settings';
import { CustomPopover } from 'src/components/custom-popover';
import { CountrySelect } from 'src/components/country-select';
import { LanguageSelect } from 'src/components/language-select';
import { varTap, varHover, transitionTap } from 'src/components/animate';

// ----------------------------------------------------------------------

export function LanguagePopover({ sx, ...other }) {
  const router = useRouter();

  const settings = useSettingsContext();

  const mdUp = useResponsive('up', 'md');

  const searchParams = useSearchParams();

  const { t, currentLang, onChangeLang } = useTranslate();

  const { open, anchorEl, onClose, onOpen } = usePopover();

  const handleChangeLang = (newLang) => {
    if (newLang) {
      onClose();
      onChangeLang(newLang);
    }
  };

  const handleChangeCountry = (countryName) => {
    if (countryName) {
      onClose();
      startTransition(() => {
        settings.setState({ countryName });

        const search = newSearchParams({
          ...Object.fromEntries(searchParams),
          country: countryName,
        });

        router.push(search, { scroll: false });
      });
    }
  };

  const renderMenuList = () => (
    <CustomPopover
      open={open}
      onClose={onClose}
      anchorEl={anchorEl}
      slotProps={{
        paper: { sx: { p: 2, pt: 4, width: 250 } },
        arrow: { placement: `top-${mdUp ? 'right' : 'left'}` },
      }}
    >
      <Stack spacing={2.5}>
        <CountrySelect
          hidePhoneCode
          id="langPopover"
          label={t('Country')}
          place={t('Choose a country')}
          value={currentLang.countryName}
          onChange={(event, newValue) => handleChangeCountry(newValue)}
        />

        <Stack direction="row">
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            <Iconify icon="solar:info-circle-line-duotone" sx={{ mr: 0.5 }} width={13} />
            {t(
              'If you change countries, item availability, prices, shipping costs, and taxes may change (including items in your cart)'
            )}
            .
          </Typography>
        </Stack>

        <LanguageSelect
          id="langPopover"
          label={t('Language')}
          value={currentLang.name}
          place={t('Choose a language')}
          onChange={(event, newValue) => handleChangeLang(newValue)}
        />

        <Stack spacing={0.5}>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {t('Currency')}
          </Typography>

          <Typography variant="body2">
            {currentLang.numberFormat?.currency} {fCurrencySymbol(currentLang.numberFormat)}
          </Typography>
        </Stack>
      </Stack>
    </CustomPopover>
  );

  return (
    <>
      <IconButton
        onClick={onOpen}
        component={m.button}
        whileTap={varTap(0.96)}
        whileHover={varHover(1.04)}
        transition={transitionTap()}
        aria-label="Languages button"
        sx={[
          (theme) => ({
            p: 0,
            width: 40,
            height: 40,
            ...(open && { bgcolor: theme.vars.palette.action.selected }),
          }),
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
        {...other}
      >
        <FlagIcon code={currentLang.countryCode} />
      </IconButton>

      {renderMenuList()}
    </>
  );
}
